import { getLogo } from '@/modules/branding/brandingService'

async function resolvePwaIconUrl(): Promise<string | null> {
  // Estrito: usa Ã­cone PWA; fallback mÃ­nimo: sidebar_icon (nunca o header)
  const safeGetLogo = async (section: any, light: boolean) => {
    try { return await getLogo(section, light) } catch { return null }
  }
  const fromPwa = (await safeGetLogo('pwa_icon' as any, true)) || (await safeGetLogo('pwa_icon' as any, false))
  if (fromPwa) return fromPwa
  const fromSidebar = (await safeGetLogo('sidebar_icon' as any, true)) || (await safeGetLogo('sidebar_icon' as any, false))
  if (fromSidebar) return fromSidebar
  return null
}

function ensureThemeMeta(color: string) {
  let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = color
}

function ensureAppleMeta(theme: string, appName: string) {
  const setMeta = (name: string, content: string) => {
    let m = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
    if (!m) { m = document.createElement('meta'); m.name = name; document.head.appendChild(m) }
    m.content = content
  }
  setMeta('apple-mobile-web-app-capable', 'yes')
  setMeta('apple-mobile-web-app-status-bar-style', 'black')
  setMeta('mobile-web-app-capable', 'yes')
  setMeta('application-name', appName)
  // iOS usa apple-touch-icon + title para o atalho
  let linkTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null
  if (!linkTitle) { linkTitle = document.createElement('meta'); (linkTitle as any).name = 'apple-mobile-web-app-title'; document.head.appendChild(linkTitle) }
  linkTitle.setAttribute('content', appName)
}

export async function attachDynamicManifest() {
  const hasStaticManifest = !!document.querySelector('link[rel="manifest"]:not(#pwa-manifest)')
  const rawIconUrl = await resolvePwaIconUrl()
  const name = 'Cripto Alert'
  const theme = '#1b1b1b'
  ensureThemeMeta(theme)
  ensureAppleMeta(theme, name)
  const fallbackGlyph = rawIconUrl ? null : composeFallbackGlyph(512, '#ffffff', getInitials(name))
  const iconSource = rawIconUrl || fallbackGlyph

  // Se já existe manifest estático no index.html, não sobrescrever com blob.
  // Isso melhora a instalabilidade no Chrome (desktop), evitando múltiplos manifests.
  if (hasStaticManifest) {
    await attachAppleTouchIcons(iconSource, theme)
    return
  }

  const manifest: any = {
    name,
    short_name: name,
    start_url: '/app',
    scope: '/',
    display: 'standalone',
    background_color: theme,
    theme_color: theme,
    icons: [],
  }

  if (iconSource) {
    const icon192 = await composeIcon(iconSource, 192, theme)
    const icon512 = await composeIcon(iconSource, 512, theme)
    manifest.icons = [
      { src: icon192, sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: icon512, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
    ]
    try {
      const badge72 = await composeMonochromeBadge(iconSource, 72)
      await putIntoCache('/icons/icon-192.png', icon192)
      await putIntoCache('/icons/badge-72x72.png', badge72)
    } catch {}
  }

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
  const href = URL.createObjectURL(blob)
  let link = document.querySelector('link#pwa-manifest[rel="manifest"]') as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'manifest'
    link.id = 'pwa-manifest'
    document.head.appendChild(link)
  }
  link.href = href

  // iOS (Safari) nÃ£o usa manifest para Ã­cone do app; injeta apple-touch-icon
  await attachAppleTouchIcons(iconSource, theme)
}

async function attachAppleTouchIcons(iconUrl: string | null, bg: string) {
  const sizes = [180, 167, 152]
  for (const size of sizes) {
    const id = `pwa-apple-icon-${size}x${size}`
    let link = document.querySelector(`link#${id}[rel="apple-touch-icon"]`) as HTMLLinkElement | null
    if (iconUrl) {
      const composed = await composeIcon(iconUrl, size, bg)
      if (!link) {
        link = document.createElement('link')
        link.rel = 'apple-touch-icon'
        link.id = id
        document.head.appendChild(link)
      }
      link.sizes = `${size}x${size}`
      link.href = composed
    } else {
      if (link && link.parentNode) link.parentNode.removeChild(link)
    }
  }
}

async function putIntoCache(path: string, dataUrl: string) {
  try {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    const cache = await caches.open('pwa-static')
    await cache.put(path, new Response(blob, { headers: { 'Content-Type': 'image/png', 'Cache-Control': 'public, max-age=604800' } }))
  } catch {}
}

async function composeMonochromeBadge(srcUrl: string, size: number): Promise<string> {
  try {
    const img = await loadImage(srcUrl)
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, size, size)
    const scale = Math.min(size / img.width, size / img.height) * 0.8
    const w = Math.round(img.width * scale)
    const h = Math.round(img.height * scale)
    const x = Math.round((size - w) / 2)
    const y = Math.round((size - h) / 2)
    ctx.drawImage(img, x, y, w, h)
    ctx.globalCompositeOperation = 'source-in'
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, size, size)
    return canvas.toDataURL('image/png')
  } catch {
    const c = document.createElement('canvas')
    c.width = size; c.height = size
    const ctx = c.getContext('2d')!
    ctx.clearRect(0,0,size,size)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(size*0.25, size*0.25, size*0.5, size*0.5)
    return c.toDataURL('image/png')
  }
}

async function composeIcon(srcUrl: string, size: number, bg: string): Promise<string> {
  try {
    const img = await loadImage(srcUrl)
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, size, size)
    // contain com margem
    const scale = Math.min(size / img.width, size / img.height) * 0.9
    const w = Math.round(img.width * scale)
    const h = Math.round(img.height * scale)
    const x = Math.round((size - w) / 2)
    const y = Math.round((size - h) / 2)
    ctx.drawImage(img, x, y, w, h)
    return canvas.toDataURL('image/png')
  } catch {
    return srcUrl // fallback: usa a imagem original sem fundo
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

function getInitials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'CA'
  const letters = parts.map((p) => p[0]).join('')
  return letters.slice(0, 2).toUpperCase()
}

function composeFallbackGlyph(size: number, color: string, text: string): string | null {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const fontSize = Math.round(size * 0.5)
    ctx.font = `700 ${fontSize}px sans-serif`
    ctx.fillText(text || 'CA', size / 2, size / 2)
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}
